import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Truck, RefreshCw, MoreVertical, Pencil, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  plate: "", fleet_number: "", marca: "", model: "", chassi: "",
  ano_modelo: "", combustivel: "", tipo_veiculo: "",
  current_odometer: 0, current_hours: 0,
  wialon_unit_id: "", status: "ativo", consumo_medio_estimado: 0,
};

const FUELS = ["Diesel", "Diesel S10", "Gasolina", "Etanol", "Flex", "GNV", "Elétrico", "Híbrido"];
const TIPOS_VEICULO = [
  "Frota Leve (Passeio / Pick-up / Utilitário)",
  "Caminhão Toco",
  "Caminhão Traçado",
  "Caminhão Basculante",
  "Caminhão Comboio",
  "Linha Amarela (Escavadeira)",
  "Linha Amarela (Pá Carregadeira)",
  "Linha Amarela (Retro)",
  "Equipamento de Apoio",
  "Outros",
];

export default function Vehicles() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lookingUpWialon, setLookingUpWialon] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
  try {
    const { data } = await api.get("/vehicles");
    setList(data);
  } catch (e) {
    console.error(
      "Erro ao carregar veículos:",
      e
    );
  }
};
  useEffect(() => {
  let isMounted = true;

  // ---------------------------------------------------
  // 1. CARREGA A FROTA IMEDIATAMENTE
  // ---------------------------------------------------
  //
  // Isso NÃO depende do Wialon.
  // Mesmo sem token/configuração do Wialon,
  // os veículos salvos no MongoDB aparecem normalmente.
  load();

  // ---------------------------------------------------
  // 2. SINCRONIZAÇÃO AUTOMÁTICA
  // ---------------------------------------------------
  const autoSync = async () => {
    try {
      // Tenta atualizar os dados pelo Wialon.
      await api.post("/vehicles/sync-telemetry");

      // Depois da sincronização,
      // busca novamente os veículos atualizados.
      if (isMounted) {
        await load();
      }

    } catch (e) {
      // Se o Wialon estiver desligado ou sem token,
      // NÃO apagamos a frota da tela.
      //
      // Apenas registramos o erro no console.
      console.warn(
        "Wialon ainda não disponível:",
        e.response?.data?.detail || e.message
      );
    }
  };

  // ---------------------------------------------------
  // 3. REPETE A SINCRONIZAÇÃO
  // ---------------------------------------------------

  const interval = setInterval(
    autoSync,
    60000
  );

  // ---------------------------------------------------
  // 4. LIMPEZA
  // ---------------------------------------------------

  return () => {
    isMounted = false;
    clearInterval(interval);
  };
}, []);

  const syncTelemetry = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/vehicles/sync-telemetry");
      toast.success(`Telemetria sincronizada (${data.mode}): ${data.count} veículos atualizados`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSyncing(false); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      plate: v.plate || "", fleet_number: v.fleet_number || "",
      marca: v.marca || "", model: v.model || "", chassi: v.chassi || "",
      ano_modelo: v.ano_modelo || "", combustivel: v.combustivel || "",
      tipo_veiculo: v.tipo_veiculo || "",
      current_odometer: v.current_odometer || 0, current_hours: v.current_hours || 0,
      wialon_unit_id: v.wialon_unit_id || "", status: v.status || "ativo",
      consumo_medio_estimado: v.consumo_medio_estimado || 0,
    });
    setOpen(true);
  };

const lookupWialon = async () => {
  // Precisa de um Fleet ID para pesquisar.
  if (!form.fleet_number) {
    return toast.error("Informe o Fleet ID");
  }

  setLookingUpWialon(true);

  try {
    const { data } = await api.get(
      `/vehicles/wialon-lookup/${encodeURIComponent(
        form.fleet_number
      )}`
    );

    // Se o backend encontrou mais de uma unidade,
    // não escolhemos automaticamente.
    if (data.multiple) {
      console.log(
        "Unidades encontradas:",
        data.items
      );

      return toast.error(
        "Mais de uma unidade encontrada para este Fleet ID"
      );
    }

    // Preenche automaticamente os dados encontrados no Wialon.
    setForm((current) => ({
      ...current,

      wialon_unit_id:
        data.wialon_unit_id || "",

      current_odometer:
        data.odometer ?? 0,

      current_hours:
        data.hours ?? 0,

      // Por enquanto usamos o nome da unidade como modelo
      // caso o campo modelo ainda esteja vazio.
      model:
        current.model || data.name || "",
    }));

    toast.success(
      `Unidade Wialon encontrada: ${data.name}`
    );

  } catch (e) {
    toast.error(
      formatApiError(
        e.response?.data?.detail
      )
    );

  } finally {
    setLookingUpWialon(false);
  }
};


  const save = async () => {
    if (!form.plate || !form.fleet_number || !form.model) return toast.error("Preencha placa, frota e modelo");
    if (form.current_odometer === "" || form.current_hours === "") return toast.error("Odômetro e horímetro são obrigatórios");
    const payload = {
      ...form,
      current_odometer: Number(form.current_odometer),
      current_hours: Number(form.current_hours),
      consumo_medio_estimado: Number(form.consumo_medio_estimado) || 0,
    };
    try {
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, { ...payload, active: editing.active !== false });
        toast.success("Veículo atualizado");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Veículo cadastrado");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (v) => {
    try {
      await api.patch(`/vehicles/${v.id}`, { active: v.active === false });
      toast.success(v.active === false ? "Veículo ativado" : "Veículo inativado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/vehicles/${pendingDelete.id}`);
      toast.success("Veículo excluído"); setPendingDelete(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="vehicles-page">
      <PageHeader title="Frota" subtitle="Cadastro e status dos veículos"
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={syncTelemetry} disabled={syncing} className="gap-2" data-testid="sync-telemetry-btn">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sincronizar Telemetria"}
            </Button>
            <Button className="gap-2" data-testid="new-vehicle-btn" onClick={openNew}>
              <Plus className="w-4 h-4" />Novo Veículo
            </Button>
          </div>
        } />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Veículo" : "Novo Veículo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Placa*</Label><Input data-testid="vehicle-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} /></div>
            <div><Label>Nº Frota (Fleet ID)*</Label><div className="flex gap-2"><Input data-testid="vehicle-fleet" value={form.fleet_number} onChange={(e) => setForm({...form, fleet_number: e.target.value})}placeholder="Ex.: 042"/>
                <Button type="button" variant="outline" onClick={lookupWialon} disabled={lookingUpWialon || !form.fleet_number}><RefreshCw className={`w-4 h-4 ${lookingUpWialon? "animate-spin": ""}`}/>{lookingUpWialon? "Buscando...": "Buscar"}</Button></div></div>
            <div><Label>Marca</Label><Input data-testid="vehicle-marca" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} placeholder="Volvo, Scania, Mercedes..." /></div>
            <div><Label>Modelo*</Label><Input data-testid="vehicle-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="FH 540" /></div>
            <div><Label>Chassi (VIN)</Label><Input data-testid="vehicle-chassi" value={form.chassi} onChange={(e) => setForm({ ...form, chassi: e.target.value.toUpperCase() })} className="font-mono" maxLength={17} placeholder="17 caracteres" /></div>
            <div><Label>Ano / Modelo</Label><Input data-testid="vehicle-ano" value={form.ano_modelo} onChange={(e) => setForm({ ...form, ano_modelo: e.target.value })} placeholder="2023/2024" /></div>
            <div>
              <Label>Tipo de Combustível</Label>
              <Select value={form.combustivel} onValueChange={(v) => setForm({ ...form, combustivel: v })}>
                <SelectTrigger data-testid="vehicle-fuel"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{FUELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Wialon Unit ID</Label><Input data-testid="vehicle-wialon" value={form.wialon_unit_id} readOnly placeholder="Preenchido automaticamente" className="bg-muted" /></div>
            <div className="col-span-2">
              <Label>Tipo de Veículo</Label>
              <Input list="tipo-veiculo-list" data-testid="vehicle-tipo" value={form.tipo_veiculo}
                onChange={(e) => setForm({ ...form, tipo_veiculo: e.target.value })}
                placeholder="Selecione ou digite..." />
              <datalist id="tipo-veiculo-list">
                {TIPOS_VEICULO.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div><Label>Odômetro (km)</Label><Input type="number" value={form.current_odometer} readOnly className="bg-muted"/></div>
            <div><Label>Horímetro (h)</Label><Input type="number" value={form.current_hours} readOnly className="bg-muted" /></div>
            <div className="col-span-2"><Label>Consumo médio estimado (km/L)</Label><Input type="number" step="0.01" data-testid="vehicle-consumo" value={form.consumo_medio_estimado} onChange={(e) => setForm({ ...form, consumo_medio_estimado: e.target.value })} placeholder="Ex.: 2.8" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-vehicle">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Frota</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead>Marca / Modelo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Ano</TableHead>
              <TableHead>Combustível</TableHead>
              <TableHead>Chassi</TableHead>
              <TableHead className="text-right">Odômetro</TableHead>
              <TableHead className="text-right">Horímetro</TableHead>
              <TableHead className="text-right">km/L</TableHead>
              <TableHead>Wialon</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-16">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((v) => {
              const inactive = v.active === false;
              return (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`} className={inactive ? "opacity-60" : ""}>
                  <TableCell className="font-mono font-semibold">{v.fleet_number}</TableCell>
                  <TableCell className="font-mono">{v.plate}</TableCell>
                  <TableCell className="flex items-center gap-2"><Truck className="w-4 h-4 text-muted-foreground" />{v.marca ? `${v.marca} ${v.model}` : v.model}</TableCell>
                  <TableCell className="text-xs">{v.tipo_veiculo || "—"}</TableCell>
                  <TableCell className="text-xs">{v.ano_modelo || "—"}</TableCell>
                  <TableCell className="text-xs">{v.combustivel || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{v.chassi || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{Number(v.current_odometer).toLocaleString("pt-BR")} km</TableCell>
                  <TableCell className="text-right font-mono">{Number(v.current_hours).toLocaleString("pt-BR")} h</TableCell>
                  <TableCell className="text-right font-mono text-xs">{v.consumo_medio_estimado ? Number(v.consumo_medio_estimado).toFixed(2) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{v.wialon_unit_id || "—"}</TableCell>
                  <TableCell>
                    {inactive
                      ? <Badge variant="outline" className="text-[10px] text-muted-foreground">Inativo</Badge>
                      : <StatusBadge status={v.status} />}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="w-7 h-7" data-testid={`vehicle-menu-${v.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => openEdit(v)} data-testid={`vehicle-edit-${v.id}`}>
                          <Pencil className="w-4 h-4 mr-2" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(v)} data-testid={`vehicle-toggle-${v.id}`}>
                          <Power className="w-4 h-4 mr-2" /> {inactive ? "Ativar" : "Inativar"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setPendingDelete(v)} className="text-destructive focus:text-destructive" data-testid={`vehicle-delete-${v.id}`}>
                          <Trash2 className="w-4 h-4 mr-2" /> Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir veículo?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o veículo <b>{pendingDelete?.plate}</b>? Esta ação não pode ser desfeita.
              Inspeções e movimentações históricas permanecerão registradas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vehicle-cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="vehicle-confirm-delete">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
